import { supabase } from './supabaseClient';

export const RSA_TICKET_SERVICE_TYPES = [
  'Jump Start',
  'Towing',
  'Flat Tyre',
  'Minor Repair',
  'Key Lockout',
  'Fuel Delivery',
];

export class PartnerTicketService {
  static packCompare(product, nowMs = Date.now()) {
    if (this.offerExpired(product, nowMs)) {
      const live = this.liveUnitPrice(product, nowMs);
      return { sell: live, compareAt: 0, pct: 0, show: false };
    }
    const sell = Math.max(0, Number(product?.price_inr) || 0);
    const was = Math.max(0, Number(product?.compare_at_inr) || 0);
    const show = was > sell + 0.004;
    const pct = show ? Math.round(((was - sell) / was) * 100) : 0;
    return { sell, compareAt: show ? was : 0, pct, show };
  }

  static offerExpired(product, nowMs = Date.now()) {
    if (!product?.offer_ends_at) return false;
    const t = new Date(product.offer_ends_at).getTime();
    return Number.isFinite(t) && t <= nowMs;
  }

  /** Charge / display unit. After a set timer ends, compare-at (actual) price is used. */
  static liveUnitPrice(product, nowMs = Date.now()) {
    const sell = Math.max(0, Number(product?.price_inr) || 0);
    const was = Math.max(0, Number(product?.compare_at_inr) || 0);
    if (this.offerExpired(product, nowMs) && was > sell) return was;
    return sell;
  }

  static offerCountdown(endsAt, nowMs = Date.now()) {
    if (!endsAt) return null;
    const t = new Date(endsAt).getTime();
    if (!Number.isFinite(t)) return null;
    const ms = t - nowMs;
    if (ms <= 0) return null;
    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  static savePctForQty(product, quantity = 1, nowMs = Date.now()) {
    if (!product) return 0;
    const qty = quantity === 5 || quantity === 10 ? quantity : 1;
    const bulkPct = qty === 10 ? 10 : qty === 5 ? 5 : 0;
    const unit = this.liveUnitPrice(product, nowMs);
    const list = Math.round(unit * qty * 100) / 100;
    const pay = Math.max(0, Math.round((list - (list * bulkPct) / 100) * 100) / 100);
    const cmp = this.packCompare(product, nowMs);
    const was = cmp.show ? Math.round(cmp.compareAt * qty * 100) / 100 : list;
    if (was <= pay + 0.004) return 0;
    return Math.round(((was - pay) / was) * 100);
  }

  static async listProducts() {
    const { data, error } = await supabase.rpc('admin_list_partner_ticket_products');
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  }

  static isMissingRpcError(message) {
    const m = String(message || '').toLowerCase();
    return (
      m.includes('could not find the function') ||
      m.includes('schema cache') ||
      m.includes('does not exist')
    );
  }

  static async upsertProduct(payload) {
    const price = Number(payload.price_inr);
    const creditsRaw = payload.credits_inr;
    const credits =
      creditsRaw === '' || creditsRaw == null || Number.isNaN(Number(creditsRaw))
        ? price
        : Number(creditsRaw);
    const compareRaw = payload.compare_at_inr;
    const compareAt =
      compareRaw === '' || compareRaw == null || Number.isNaN(Number(compareRaw))
        ? null
        : Number(compareRaw);

    const base = {
      p_id: payload.id || null,
      p_name: payload.name,
      p_description: payload.description || null,
      p_price_inr: price,
      p_tickets_count: Number(payload.tickets_count),
      p_allowed_service_types: payload.allowed_service_types || [],
      p_active: payload.active !== false,
      p_sort_order: Number(payload.sort_order) || 0,
      p_credits_inr: credits,
      p_compare_at_inr: compareAt,
    };
    const withLabel = {
      ...base,
      p_offer_label: payload.offer_label?.trim() || null,
    };
    const withTimer = {
      ...withLabel,
      p_offer_ends_at: payload.offer_ends_at || null,
    };

    const attempts = [withTimer, withLabel, base];
    for (const args of attempts) {
      const { data, error } = await supabase.rpc('admin_upsert_partner_ticket_product', args);
      if (!error) {
        const parsed = data || {};
        if (!parsed.success) throw new Error(parsed.error || 'Save failed');
        if (args !== withTimer && payload.offer_ends_at) {
          throw new Error(
            'Pack saved, but countdown SQL is not applied yet. Run supabase/partner-ticket-offer-timer.sql in Supabase, then save the timer again.',
          );
        }
        return parsed.product;
      }
      if (!this.isMissingRpcError(error.message)) throw error;
    }

    throw new Error(
      'Save function not found. Run these in Supabase SQL editor, then try again:\n' +
        '1) supabase/partner-ticket-compare-at.sql\n' +
        '2) supabase/partner-ticket-offer-label.sql\n' +
        '3) supabase/partner-ticket-offer-timer.sql',
    );
  }

  static async listPurchases(limit = 50) {
    const { data, error } = await supabase.rpc('admin_list_partner_ticket_purchases', {
      p_limit: limit,
    });
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  }
}
